import type { Core } from 'cytoscape'
import { isMobileViewport } from '@/hooks/use-mobile'

const PHONE_MIN_PADDING = 24
/** Right-hand strip of a phone canvas the toolbar column covers: 16 px gutter, 38 px buttons, 8 px gap. */
const PHONE_TOOLBAR_STRIP = 62

/**
 * Viewport padding for `cy.fit()` / layout `padding`.
 *
 * The desktop paddings (50–160 px per side) are sized for a wide canvas. On a
 * phone-wide canvas they eat most of the width, so the fitted graph is drawn
 * far smaller than the screen allows. Below the `md` breakpoint the padding is
 * scaled down to a third (never under 24 px, which keeps nodes clear of the
 * screen edge); from `md` up the value is returned unchanged.
 */
export function fitPadding(desktopPadding: number): number {
  if (!isMobileViewport()) return desktopPadding
  return Math.min(desktopPadding, Math.max(PHONE_MIN_PADDING, Math.round(desktopPadding / 3)))
}

function hasPhoneToolbar(cy: Core): boolean {
  if (!isMobileViewport()) return false
  const canvasRoot = cy.container()?.closest('[data-graph-canvas-root]')
  return Boolean(canvasRoot?.querySelector('[data-graph-toolbar]'))
}

/**
 * Fit the whole graph into the canvas.
 *
 * `cy.fit()` pads all four sides equally. The desktop padding is about as wide
 * as the toolbar column; the smaller phone padding is not, so nodes at the right
 * edge would sit under the toolbar buttons. On a phone the fit therefore leaves
 * that strip free and shifts the graph left; from `md` up it is a plain fit.
 */
export function fitGraph(cy: Core, desktopPadding: number): void {
  const padding = fitPadding(desktopPadding)
  const shift = hasPhoneToolbar(cy) ? Math.max(0, (PHONE_TOOLBAR_STRIP - padding) / 2) : 0
  cy.fit(undefined, padding + shift)
  if (shift > 0) cy.panBy({ x: -shift, y: 0 })
}

/**
 * Zoom floor for the cytoscape instance.
 *
 * `cy.fit()` is clamped by `minZoom`. A few hundred nodes need a lower zoom on
 * a phone-wide canvas than the desktop floor allows, so the fitted graph would
 * be drawn wider than the screen and cut off on both edges. A phone canvas
 * leaves roughly a quarter of the desktop width for the graph, so below `md`
 * the floor is a quarter of the desktop value; from `md` up it is unchanged.
 */
export function phoneMinZoom(desktopMinZoom: number): number {
  return isMobileViewport() ? desktopMinZoom / 4 : desktopMinZoom
}
